import { Controller } from "@hotwired/stimulus";

// Opens the overflow list for one calendar day in place, instead of navigating
// away from the month. A CSS-only dropdown can't do the part that matters here:
// cells in the last column and last week need the panel to flip left / above so
// it stays inside the box that clips it.
//
// Usage:
//   <div data-controller="calendar-day-popover">
//     <button data-action="calendar-day-popover#toggle">+2 more</button>
//     <div class="hidden absolute" data-calendar-day-popover-target="panel">…</div>
//   </div>
export default class extends Controller<HTMLElement> {
  static targets = ["panel"];

  declare readonly panelTarget: HTMLElement;
  declare readonly hasPanelTarget: boolean;

  connect() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
  }

  toggle(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.hasPanelTarget) return;

    if (this.panelTarget.classList.contains("hidden")) {
      this.open();
    } else {
      this.close();
    }
  }

  close() {
    if (!this.hasPanelTarget) return;
    this.panelTarget.classList.add("hidden");
  }

  private open() {
    this.closeOthers();
    this.panelTarget.classList.remove("hidden");
    this.position();
  }

  // Only one day open at a time — two overlapping panels in adjacent cells are
  // unreadable.
  private closeOthers() {
    document
      .querySelectorAll<HTMLElement>("[data-calendar-day-popover-target='panel']")
      .forEach((panel) => {
        if (panel !== this.panelTarget) panel.classList.add("hidden");
      });
  }

  private position() {
    const panel = this.panelTarget;
    panel.classList.remove("right-0", "bottom-full", "mb-1");
    panel.classList.add("left-0", "top-full", "mt-1");

    const rect = panel.getBoundingClientRect();
    const margin = 8;

    if (rect.right > this.rightEdge() - margin) {
      panel.classList.remove("left-0");
      panel.classList.add("right-0");
    }

    if (rect.bottom > window.innerHeight - margin) {
      panel.classList.remove("top-full", "mt-1");
      panel.classList.add("bottom-full", "mb-1");
    }
  }

  // The window is the wrong edge to flip against: the month sits in a page
  // sheet that clips and scrolls, and with the assistant docked that sheet ends
  // hundreds of pixels short of the window. Flip against the nearest box that
  // actually cuts the panel off.
  private rightEdge(): number {
    let el: HTMLElement | null = this.element.parentElement;
    while (el) {
      const overflowX = getComputedStyle(el).overflowX;
      if (overflowX === "auto" || overflowX === "scroll" || overflowX === "hidden") {
        return el.getBoundingClientRect().right;
      }
      el = el.parentElement;
    }
    return window.innerWidth;
  }

  private onDocumentClick = (event: MouseEvent) => {
    if (this.element.contains(event.target as Node)) return;
    this.close();
  };

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.close();
  };
}
